import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { env } from "../../../config/environment";
import { createActionReporter } from "../../../diagnostics";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

export interface MeetingSource {
  id: string;
  type: string;
  name: string;
  slug: string;
  normalizerKey: string;
  signatureHeader?: string | null;
  enabled: boolean;
  hasSigningSecret: boolean;
  webhookPath: string;
}

export const SOURCE_TYPES = ["read_ai", "generic_webhook", "fireflies", "fathom", "manual"];

/** View store for the "Meeting Sources" admin view (meeting-source-connections). */
class MeetingSourcesStore {
  rootStore: RootStore;
  loading = false;
  error: string | null = null;

  newType = "generic_webhook";
  newName = "";
  newSlug = "";
  newSecret = "";
  saving = false;
  formError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("meeting-source-connections") ?? this.rootStore.meetingSourceConnectionsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  get sources(): MeetingSource[] {
    return (this.service?.entities ?? []) as unknown as MeetingSource[];
  }

  webhookUrl(source: MeetingSource): string {
    const base = (env.apiUrl || "").replace(/\/+$/, "");
    return `${base}${source.webhookPath || `/meeting-webhook/${source.slug}`}`;
  }

  setField(field: "newType" | "newName" | "newSlug" | "newSecret", value: string) {
    this[field] = value;
  }

  async load() {
    this.loading = true;
    this.error = null;
    try {
      // findOrThrow: the ServiceStore resolves null on a failed find, which
      // rendered the "none yet" empty state.
      await findOrThrow(this.service, { query: { $limit: 200, $sort: { name: 1 } } });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load meeting sources");
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
  async reload() {
    await this.load();
  }

  async addSource(): Promise<void> {
    if (!this.newName.trim() || !this.newSlug.trim()) {
      this.formError = "Name and slug are required.";
      return;
    }
    this.saving = true;
    this.formError = null;
    try {
      const payload: Record<string, unknown> = {
        type: this.newType,
        normalizerKey: this.newType,
        name: this.newName.trim(),
        slug: this.newSlug.trim().toLowerCase(),
      };
      if (this.newSecret.trim()) payload.signingSecret = this.newSecret.trim();
      await this.service?.create(payload as never);
      runInAction(() => {
        this.newName = "";
        this.newSlug = "";
        this.newSecret = "";
      });
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.formError = err instanceof Error ? err.message : "Failed to add source";
      });
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /**
   * Report a failed user-initiated action: toast to the user AND a structured
   * `view.action_failed` diagnostic into the retained ring buffer. Replaces the
   * `console.error` these two paths used to end in, which told the user nothing
   * — they clicked Delete, the row stayed, and the app was silent.
   */
  private reportFailure = createActionReporter(
    () => this.rootStore,
    "MeetingSourcesStore",
  );

  /** Rotate the signing secret on a source (patch with the write-only field). */
  async rotateSecret(id: string, secret: string): Promise<void> {
    if (!secret.trim()) return;
    try {
      await this.service?.patch(id, { signingSecret: secret.trim() });
      await this.reload();
    } catch (err) {
      this.reportFailure("rotate the signing secret", err, { data: { id } });
    }
  }

  async removeSource(id: string) {
    try {
      await this.service?.remove(id);
      await this.reload();
    } catch (err) {
      this.reportFailure("remove the meeting source", err, { data: { id } });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.newType = "generic_webhook";
    this.newName = "";
    this.newSlug = "";
    this.newSecret = "";
    this.saving = false;
    this.formError = null;
  }
}

export default MeetingSourcesStore;
